// Dynamic specialty index — lists algorithms of one specialty.
// Route: /algorithm/:specialty

import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Heart, Wind, Zap, Baby, Layers } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { SPECIALTIES, specialtyCounts } from "@/lib/algorithms";
import { cn } from "@/lib/utils";

const ICONS = { cardiovascular: Heart, respiratory: Wind, neurology: Zap, pediatric: Baby, anestesi: Layers };

const ACCENT = {
  teal: "text-teal-700 border-teal-700/40 bg-teal-700/5",
  sky: "text-sky-700 border-sky-700/40 bg-sky-700/5",
  purple: "text-purple-700 border-purple-700/40 bg-purple-700/5",
  amber: "text-amber-700 border-amber-700/40 bg-amber-700/5",
  stone: "text-stone-700 border-stone-300 bg-stone-100",
};

function pick(t, lang) {
  return typeof t === "string" ? t : t[lang] || t.en || t.id || "";
}

export default function AlgorithmSpecialty() {
  const { specialty: specKey } = useParams();
  const { lang, t } = useLanguage();

  const specialty = SPECIALTIES[specKey];
  if (!specialty) return <Navigate to="/" replace />;

  const Icon = ICONS[specKey] || Layers;
  const accent = ACCENT[specialty.color] || ACCENT.sky;
  const counts = specialtyCounts(specKey);

  const items = specialty.order.map((id) => specialty.algorithms[id]).filter(Boolean);

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-10 py-8 md:py-12" data-testid={`algorithm-specialty-${specKey}`}>
      <div className="mb-6">
        <Link
          to="/"
          data-testid="algo-spec-back-home"
          className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.14em] uppercase text-stone-500 font-mono hover:text-sky-700 transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.5} />
          {t("algo.back_home")}
        </Link>
      </div>

      <header className="border-b border-stone-200 pb-5 mb-8">
        <div className={cn("text-[11px] tracking-[0.18em] uppercase font-semibold mb-2 flex items-center gap-2", accent.split(" ")[0])}>
          <Icon size={14} strokeWidth={1.5} />
          {pick(specialty.name, lang)}
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-medium text-stone-900 tracking-tight leading-tight">
          {pick(specialty.name, lang)}
        </h1>
        <p className="mt-3 text-sm md:text-base text-stone-600 leading-relaxed max-w-2xl">
          {t("algo.list.intro")}
        </p>
        <div className="mt-3 text-xs font-mono text-stone-500">
          {t("algo.count").replace("{n}", `${counts.available}/${counts.total}`)}
        </div>
      </header>

      <ul className="space-y-2" data-testid="algo-list">
        {items.map((algo) => {
          const title = pick(algo.title, lang);
          const desc = pick(algo.desc, lang);
          if (algo.available) {
            return (
              <li key={algo.id}>
                <Link
                  to={`/algorithm/${specKey}/${algo.id}`}
                  data-testid={`algo-item-${algo.id}`}
                  className={cn(
                    "group flex items-center justify-between gap-3 border rounded-sm p-4 bg-white hover:shadow-sm transition-all",
                    accent
                  )}
                >
                  <div>
                    <div className="text-sm md:text-base font-medium text-stone-900">{title}</div>
                    {desc && <div className="text-xs text-stone-600 mt-0.5">{desc}</div>}
                  </div>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border border-current rounded-full opacity-80">
                      {t("algo.badge.ready")}
                    </span>
                    <ArrowUpRight size={16} strokeWidth={1.5} className="opacity-60 group-hover:opacity-100" />
                  </span>
                </Link>
              </li>
            );
          }
          return (
            <li
              key={algo.id}
              data-testid={`algo-item-${algo.id}`}
              className="flex items-center justify-between gap-3 border border-stone-200 bg-stone-50 rounded-sm p-4 opacity-70 cursor-not-allowed"
              aria-disabled="true"
            >
              <div>
                <div className="text-sm md:text-base font-medium text-stone-700">{title}</div>
                {desc && <div className="text-xs text-stone-500 mt-0.5">{desc}</div>}
              </div>
              <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border border-stone-300 text-stone-500 rounded-full">
                {t("algo.badge.soon")}
              </span>
            </li>
          );
        })}
      </ul>

      <div className="mt-10 max-w-2xl">
        <div className="border-l-2 border-amber-700/60 pl-4 py-1 text-xs text-stone-600 leading-relaxed">
          <span className="font-semibold text-stone-900">{t("algo.disclaimer.label")}</span>{" "}
          {t("algo.disclaimer.body")}
        </div>
      </div>
    </div>
  );
}
